import React, { Component } from 'react';
import { Redirect, Link, withRouter } from 'react-router-dom';

import Markdown from 'markdown-to-jsx'

import HelpText from '../../components/help/HelpText';

import { lowerSnakeCase, adminUser, renderErrorWarning, renderErrors } from '../../utilities/Forms.js'
import { formatDate } from '../../utilities/Generic.js'
import { request, reportErrorToHoneybadger } from '../../helpers/Requests'

class EmailTemplates extends Component {
  constructor(props) {
    super(props);

    this.handleChange = this.handleChange.bind(this);
    this.insertVariable = this.insertVariable.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.handleBack = this.handleBack.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);

    this.textareaRef = React.createRef();
  }

  state = {
    name: null,
    body: null,

    location_name: "",
    device_name: "",
    profile_name: "",
    custom_attributes: [],

    locationLoaded: false,
    deviceLoaded: false,
    profileLoaded: false,
    customAttributesLoaded: false,
    type_of_organisation: localStorage.getItem("type_of_organisation"),

    unauthorized: "",
    error: "",
    errors: ""
  };

  static emails = {
    "person_arrival": {
      name: "Person Arrival Mailer",
      description: "This is the Person Arrival Mailer. It gets sent to the email address of the person arriving.",
      default_body: `# You checked in at {{location_name}} - {{organisation_name}}

        Hi {{person_first_name}}

        Thanks for checking in at {{location_name}}.

        {{text_emailed_on_check_in}}

        {{link_emailed_on_check_in}}

        {{organisation_name}}

        ---

        Digital Reception`,
      variables: ["Link Emailed On Check In", "Location Name", "Organisation Name", "Person First Name", "Text Emailed On Check In"],
      custom_attributes: true
    },
    "person_leaving": {
      name: "Person Leaving Mailer",
      description: "This is the Person Leaving Mailer. It gets sent to the email address of the person leaving.",
      default_body: `# You checked out at {{location_name}} - {{organisation_name}}

        Hi {{person_first_name}}

        Thanks for checking out at {{location_name}}.

        {{text_emailed_on_check_out}}

        {{link_emailed_on_check_out}}

        {{organisation_name}}

        ---

        Digital Reception`,
      variables: ["Link Emailed On Check Out", "Location Name", "Organisation Name", "Person First Name", "Text Emailed On Check Out"],
      custom_attributes: true
    },
    "booking_confirmation": {
      name: "Booking Confirmation Mailer",
      description: "This is the Booking Confirmation Mailer. It gets sent to the Visitor when an Appointment is created.",
      default_body: `# Booking Confirmation from {{organisation_name}}

        Dear {{person_first_name}} {{person_last_name}},

        This is confirmation that you have been booked in for a visit to {{location_name}}, at {{appointment_start_time}}, with a scheduled end of {{appointment_end_time}}.

        This booking is titled *{{title}}*

        {{list_of_visitors}}

        {{notes}}

        ---

        Thanks!

        Digital Reception`,
      variables: ["Organisation Name", "Person First Name", "Person Last Name", "Location Name", "Appointment Start Time", "Appointment End Time", "Title", "List Of Visitors", "Notes"],
      custom_attributes: true
    },
    "booking_amendment": {
      name: "Booking Amendment Mailer",
      description: "This is the Booking Amendment Mailer. It gets sent to the Visitor when an Appointment is modified.",
      default_body: `# Booking Amendment from {{organisation_name}}

        Dear {{person_first_name}} {{person_last_name}},

        This is confirmation that your previous booking at {{location_name}}, titled *{{title}}*, has been altered.

        {{changes}}

        {{list_of_visitors}}

        {{notes}}

        ---

        Thanks!

        Digital Reception`,
      variables: ["Changes", "List Of Visitors", "Location Name", "Notes", "Organisation Name", "Person First Name", "Person Last Name", "Title"],
      custom_attributes: true
    },
    "booking_reminder": {
      name: "Booking Reminder Mailer",
      description: "This is the Booking Reminder Mailer. It gets sent to the Visitor one day before the Appointment date.",
      default_body: `# Booking Reminder from {{organisation_name}}

        Dear {{person_first_name}} {{person_last_name}},

        This is a reminder that you have an upcoming appointment for a scheduled visit at {{location_name}}, titled *{{title}}*, which starts at {{appointment_start_time}}, and ends at {{appointment_end_time}}.

        {{list_of_visitors}}

        {{link}}

        {{notes}}

        ---

        Thanks!

        Digital Reception`,
      variables: ["Link", "List Of Visitors", "Location Name", "Notes", "Organisation Name", "Person First Name", "Person Last Name", "Appointment End Time", "Appointment Start Time", "Title"],
      custom_attributes: true
    },
    "booking_removal": {
      name: "Booking Removal Mailer",
      description: "This is the Booking Removal Mailer. It gets sent to the Visitor when an Appointment is deleted.",
      default_body: `# Booking CANCELLATION from {{organisation_name}}

        Dear {{person_first_name}} {{person_last_name}},

        This is confirmation that you are **NO LONGER** scheduled in for your previously scheduled visit at {{location_name}}, on {{appointment_start_time}}.

        {{list_of_visitors}}

        ---

        Thanks!

        Digital Reception`,
      variables: ["List Of Visitors", "Location Name", "Organisation Name", "Person First Name", "Person Last Name", "Appointment Start Time"],
      custom_attributes: true
    },
    "arrival": {
      internal: true,
      name: "Arrival Mailer",
      description: "",
      default_body: `# Arrival at {{location_name}} - {{organisation_name}}

        This person arrived under the name {{person_first_name}} {{person_last_name}}.

        {{group_members}}

        Their arrival time was {{arrival_time}}.

        You received this email as you are listed as the arrival email notification recipient for {{profile_name}} at {{organisation_name}}.

        ---

        Digital Reception`,
      variables: ["Person First Name", "Person Last Name", "Group Members", "Arrival Time", "Profile Name", "Location Name", "Organisation Name"],
      custom_attributes: true
    },
    "departure": {
      internal: true,
      name: "Departure Mailer",
      description: "",
      default_body: `# Departure at {{location_name}} - {{organisation_name}}

        This person departed under the name {{person_first_name}} {{person_last_name}}.

        {{left_with}}

        Their departure time was {{departure_time}}.

        You received this email as you are listed as the departure email notification recipient for {{profile_name}} at {{organisation_name}}.

        ---

        Digital Reception`,
      variables: ["Person First Name", "Person Last Name", "Left With", "Departure Time", "Profile Name", "Location Name", "Organisation Name"],
      custom_attributes: true
    },
    "red_list": {
      internal: true,
      name: "Red List Mailer",
      description: "",
      default_body: `# Arrival at {{location_name}} - {{organisation_name}}

        {{person_first_name}} {{person_last_name}} has arrived, and they are set to have warning emails sent whenever they arrive.

        Their arrival time was {{arrival_time}}.

        You are receiving this email because you are listed as a critical alert recipient for {{organisation_name}} and are assigned to {{location_name}}.

        ---

        Digital Reception`,
      variables: ["Person First Name", "Person Last Name", "Arrival Time", "Location Name", "Organisation Name"],
      custom_attributes: true
    },
    "feedback_alert": {
      internal: true,
      name: "Feedback Alert Mailer",
      description: "",
      default_body: `At {{event_time}} on {{event_type}} at {{location_name}}, {{person_first_name}} {{person_last_name}} gave the following answer(s):

        {{answer_block}}

        ---

        Digital Reception`,
      variables: ["Answer Block", "Person First Name", "Person Last Name", "Location Name", "Event Time", "Event Type"],
      custom_attributes: true
    },
    "offline_device": {
      internal: true,
      name: "Offline Device Mailer",
      description: "",
      default_body: `# Offline Device warning for {{location_name}}'s {{device_name}}

        At {{location_name}}, no physical device logged into the {{device_name}} has been seen online since {{last_seen_online}} when this email was sent at {{current_time}}.

        Any physical device(s) logged in under this Device may be having wifi connection issues or seperate technical issues that may need follow up or investigation.

        You are receiving this email because you are listed as a critical alert recipient for {{organisation_name}} and are assigned to {{location_name}}.

        ---

        Digital Reception`,
      variables: ["Current Time", "Last Seen Online", "Device Name", "Location Name", "Organisation Name"],
      custom_attributes: false
    },
    "booking_form_notification": {
      internal: true,
      retail_only: true,
      name: "Booking Form Notification",
      description: "This is the Booking Form Notification. It is sent to Admins assigned to a Location when the Booking Form is used to make an Appointment.",
      default_body: `# New Booking at {{location_name}}

        Dear {{user_name}},

        This is confirmation that a new booking has been created for {{arriving_person_name}} to see {{host_person_name}} at {{appointment_start_time}}.

        This booking is scheduled to last until {{appointment_end_time}}.

        This booking is titled *{{title}}*.

        ---

        Thanks!

        Digital Reception
      `,
      variables: ["User Name", "Arriving Person Name", "Host Person Name", "Location Name", "Title", "Appointment Start Time", "Appointment End Time"],
      custom_attributes: false,
    }
  }

  static variables = {
    answer_block: "Question: How would you rate your overall experience\n\nAnswer: 5/5 Stars\n\nQuestion: Do you have any additional feedback\n\nAnswer: I was glad there was a lot of parking space",
    appointment_end_time: `3:00pm, ${formatDate(new Date(), 7, "/", "dayFirst")}`,
    appointment_start_time: `10:00am, ${formatDate(new Date(), 7, "/", "dayFirst")}`,
    arrival_time: `12pm, on ${formatDate(new Date(), 1, "/", "dayFirst")}`,
    changes: `The expected arrival time was previously 10am ${formatDate(new Date(), 7, "/", "dayFirst")} but it is now at 3pm ${formatDate(new Date(), 7, "/", "dayFirst")}.`,
    current_time: `${formatDate(new Date(), 0, "/", "dayFirst")} ${new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(' ','').toLowerCase()}`,
    device_name: "<replaced by this.state.device_name>",
    departure_time: `12pm, on ${formatDate(new Date(), 1, "/", "dayFirst")}`,
    event_time: "9:05am",
    event_type: "Departure",
    group_members: "They arrived with these people:\n\n* Jane Smith\n\n* Johnny Smith",
    last_seen_online: `${formatDate(new Date(), -1, "/", "dayFirst")} 8:37am`,
    left_with: "They departed with these people:\n\n* Jane Smith\n\n* Johnny Smith",
    link: "[Visit our website](https://app.dreception.com)",
    link_emailed_on_check_in: "[View our privacy policy](https://www.dreception.com/privacy)",
    link_emailed_on_check_out: "[Check our website for opening hours](https://app.dreception.com)",
    list_of_visitors: "## Visitors\n\nParticipants in the event are as follows\n\n* Johnny Smith\n* Jane Smith\n* John Smith",
    location_name: "<replaced by this.state.location_name>",
    notes: "Please bring a cake.",
    organisation_name: localStorage.organisationName,
    person_first_name: "John",
    person_last_name: "Smith",
    profile_name: "<replaced by this.state.profile_name>",
    text_emailed_on_check_in: "Don't forget to collect your raincoat on the way out.",
    text_emailed_on_check_out: "Did you remember your rain coat?",
    title: localStorage.getItem("type_of_organisation") === "Retail" ? "Balayage for Jane Doe by John Smith" : "Summer Fete",
    user_name: "Administrator",
    host_person_name: "John Smith",
    arriving_person_name: "Jane Doe",
  }

  handleChange(event) {
    let name = event.target.name;
    let value = event.target.value;

    this.setState({[name]: value});
  }

  insertVariable(event) {
    const variable = `{{${event.target.getAttribute("name")}}}`;
    const textarea = this.textareaRef.current;

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    let body = textarea.value

    if (start && end) {
      const insertSpace = textarea.value.slice(end).charAt(0) === " " ? "" : " "

      this.setState({
        body: body.slice(0, start) + variable + insertSpace + body.slice(end)
      })
    }
    else {
      this.setState({
        body: body + "\n" + variable
      })
    }
  };

  handleBack() {
    this.props.history.push('/settings/email-templates');
  }

  handleReset() {
    const email = EmailTemplates.emails[this.state.name]
    this.setState({body: email.default_body.split('\n').map(line => line.trimStart()).join('\n')})
  }

  handleSubmit(event) {
    this.setState({
      updating: true
    })

    let body = this.state.body

    if (body === null) {
      // They clicked save without editing the template.
      // Send the default body for them to avoid a `Body can't be blank` error
      body = this.textareaRef.current.value
      this.setState({ body: this.textareaRef.current.value })
    }

    const json = JSON.stringify({
      "email_template": {
        "body": body
      }
    })

    request('PUT', `/v1/a/email_templates/${this.state.name}`, json, () => {this.setState({ unauthorized: true })})
      .then(data => {
        this.setState({
          updating: false,
          errors: "",
        })

        sessionStorage.setItem("updateSuccess", `Email template updated successfully!`);
        this.props.updatePopupCallback(Math.random())
      })
      .catch(error => {
        reportErrorToHoneybadger(error)

        if (error instanceof Error) {
          this.setState({ updating: false, error: error })
        }
        else {
          this.setState({ updating: false, errors: error })
        }
      })

    event.preventDefault();
  }

  fetchData(name) {
    this.setState({locationLoading: true})

    request('GET', `/v1/a/email_templates/${name}`, null, () => {this.setState({ unauthorized: true })})
      .then(data => {
        this.setState({
          body: data.body,
          loading: false
        })
      })
      .catch(error => {
        reportErrorToHoneybadger(error)

        if (error instanceof Error) {
          this.setState({ loading: false, error: error })
        }
        else {
          this.setState({ loading: false, errors: error })
        }
      })
  }

  renderSaveButtons() {
    if (adminUser()) {
      return (
        <>
          <div className="button new-button red-button" onClick={this.handleReset}>Reset</div>
          <input type="submit" value="Save" />
          {this.state.updating === true && "Saving..."}
        </>
      )
    }
    else {
      return <input type="submit" value="Sorry, you don't have permission to save changes" disabled="disabled" />
    }
  }

  replaceVariables(body, allowed_variables) {
    Object.keys(EmailTemplates.variables).forEach((variable) => {
      const pattern = new RegExp(`{{${variable}}}`, 'g');
      let string = EmailTemplates.variables[variable]

      if (variable === "location_name") {
        string = this.state.location_name
      }

      if (variable === "device_name") {
        string = this.state.device_name
      }

      if (variable === "profile_name") {
        string = this.state.profile_name
      }

      if (allowed_variables.includes(variable)) {
        body = body.replace(pattern, string);
      }
    })

    return body
  }

  replaceCustomAttributes(body, allowed_variables) {
    this.state.custom_attributes.forEach((custom_attribute) => {
      const variable = lowerSnakeCase(custom_attribute.name)
      const pattern = new RegExp(`{{${variable}_[0-9]+}}`, 'g');

      let string = `John Smith's ${custom_attribute.name}`

      if (allowed_variables.includes(variable)) {
        body = body.replace(pattern, string);
      }
    })

    return body
  }

  replaceEverything(body, allowed_variables, allowed_custom_attributes) {
    body = this.replaceVariables(body, allowed_variables.map(variable => lowerSnakeCase(variable)))

    if (EmailTemplates.emails[this.state.name]["custom_attributes"] === true) {
      body = this.replaceCustomAttributes(body, allowed_custom_attributes.map(custom_attribute => lowerSnakeCase(custom_attribute.name)))
    }

    return body
  }


  render() {
    const { name, body, custom_attributes, loading, locationLoaded, deviceLoaded, profileLoaded, customAttributesLoaded, type_of_organisation, unauthorized, errors, error } = this.state;

    if (localStorage.license === "entry") {
      return <Redirect to="/upgrade" />
    }

    if (unauthorized) {
      return <Redirect to="/login"/>
    }

    if (locationLoaded === false || deviceLoaded === false || profileLoaded === false || customAttributesLoaded === false) {
      return <p>Loading ...</p>;
    }

    if (error) {
      return <div>{error.message}</div>;
    }

    if (name === null) {
      return (
        <div>
          <h3>Customer facing emails</h3>
          {Object.keys(EmailTemplates.emails).map((name) => {
            if (EmailTemplates.emails[name]["internal"] === true) {
              return null
            }

            if (EmailTemplates.emails[name]["retail_only"] === true && type_of_organisation !== "Retail") {
              return null
            }

            return (
              <Link to={`/settings/email-templates/${name}`} key={name}>
                <div className="widget exports-page">
                  <h3 className="title">{EmailTemplates.emails[name]["name"]}</h3>
                </div>
              </Link>
            )
          })}

          <h3>Staff facing emails</h3>
          {Object.keys(EmailTemplates.emails).map((name) => {
            if (EmailTemplates.emails[name]["internal"] !== true) {
              return null
            }

            if (EmailTemplates.emails[name]["retail_only"] === true && type_of_organisation !== "Retail") {
              return null
            }

            return (
              <Link to={`/settings/email-templates/${name}`} key={name}>
                <div className="widget exports-page">
                  <h3 className="title">{EmailTemplates.emails[name]["name"]}</h3>
                </div>
              </Link>
            )
          })}
        </div>
      )
    }

    if (name) {
      const email = EmailTemplates.emails[name]
      const bodyText = body === null ? email.default_body.split('\n').map(line => line.trimStart()).join('\n') : body

      return (
        <form className="settings big-settings top-padding email-template-page" onSubmit={this.handleSubmit}>
          <div className="button small-button float-right negative-right-margin negative-top-margin" onClick={this.handleBack}>Back</div>

          <div className="row">
            <h4 className="small-bottom-margin no-top-margin">{email.name}</h4>
            <p>{email.description}</p>
          </div>

          <div className="row email-body">
            <div>
              <HelpText page={'emailTemplates'} section={'body'} className="margin-right" />
              <label className="column">Body:</label>{ renderErrors(errors, 'body') }

              {loading === true ? (
                <>Loading...</>
              ) : (
                <textarea name="body" value={bodyText} onChange={this.handleChange} ref={this.textareaRef} />
              )}
            </div>
            <div>
              <HelpText page={'emailTemplates'} section={'preview'} />
              <label className="column">Preview:</label>

              {loading === true ? (
                <>Loading...</>
              ) : (
                <div className="email-preview"><Markdown options={{ disableParsingRawHTML: true, disableAutoLink: true }} children={this.replaceEverything(bodyText, email.variables, custom_attributes)} /></div>
              )}
            </div>

            <div>
              <HelpText page={'emailTemplates'} section={'variables'} className="margin-right" />
              <label className="column">Variables:</label>

              <div className="variables">
                {email.variables.map((variable) => (
                  <div className="tooltip-container" key={variable}>
                    <div className="variable-button tooltip-trigger" name={lowerSnakeCase(variable)} onClick={this.insertVariable}>{variable}</div>
                    <span className="tooltip below"><Markdown children={this.replaceVariables(`{{${lowerSnakeCase(variable)}}}`, lowerSnakeCase(variable))} /></span>
                  </div>
                ))}
              </div>

              {EmailTemplates.emails[name]["custom_attributes"] === true && (
                <>
                  <HelpText page={'emailTemplates'} section={'custom_attributes'} className="margin-right margin-top" />
                  <label className="column">Custom Attributes:</label>

                  <div className="variables">
                    {custom_attributes.map((custom_attribute) => (
                      <div className="tooltip-container" key={custom_attribute.id}>
                        <div className="variable-button tooltip-trigger" name={`${lowerSnakeCase(custom_attribute.name)}_${custom_attribute.id}`} onClick={this.insertVariable}>{custom_attribute.name}</div>
                        <span className="tooltip below"><Markdown children={this.replaceCustomAttributes(`{{${lowerSnakeCase(custom_attribute.name)}_${custom_attribute.id}}}`, [lowerSnakeCase(custom_attribute.name)])} /></span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="save-container">
              { renderErrorWarning(errors) }
              <div className="save-buttons">
                { this.renderSaveButtons() }
              </div>
            </div>
          </div>
        </form>
      )
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.location === prevProps.location) {
      return
    }

    if (this.props.location.pathname.endsWith("/email-templates")) {
      this.setState({ name: null });
    }
    else if (!this.props.location.pathname.endsWith("/email-templates")) {
      const name = this.props.location.pathname.split("/").pop()

      this.setState({
        name: name,
        error: "",
        errors: "",
        loading: true
      });

      this.fetchData(name)
    }
  }

  componentDidMount() {
    if (!this.props.location.pathname.endsWith("/email-templates")) {
      const name = this.props.location.pathname.split("/").pop()

      this.setState({
        name: name,
        loading: true
      });

      this.fetchData(name)
    }

    request('GET', `/v1/a/locations/names_and_ids`, null, () => { this.setState({ unauthorized: true }) })
      .then(data => {
        this.setState({ location_name: data[0]?.name || "Location name", locationLoaded: true })
      })
      .catch(error => {
        reportErrorToHoneybadger(error)

        this.setState({ error, locationLoaded: true })
      })

    request('GET', `/v1/a/devices/names_and_ids`, null, () => { this.setState({ unauthorized: true }) })
      .then(data => {
        this.setState({ device_name: data[0]?.name || "Device name", deviceLoaded: true })
      })
      .catch(error => {
        reportErrorToHoneybadger(error)

        this.setState({ error, deviceLoaded: true })
      })

    request('GET', `/v1/a/profiles/names_and_ids`, null, () => { this.setState({ unauthorized: true }) })
      .then(data => {
        this.setState({ profile_name: data[0]?.name || "Profile name", profileLoaded: true })
      })
      .catch(error => {
        reportErrorToHoneybadger(error)

        this.setState({ error, profileLoaded: true })
      })

    request('GET', `/v1/a/custom_attributes/names_and_ids`, null, () => { this.setState({ unauthorized: true }) })
      .then(data => {
        this.setState({
          custom_attributes: data,
          customAttributesLoaded: true
        })
      })
      .catch(error => {
        reportErrorToHoneybadger(error)

        this.setState({ error, customAttributesLoaded: true })
      })
  }
}

export default withRouter(EmailTemplates);
